// src/components/dev/utils/userHelper.ts
import { UserRole, UserRoleType } from "../../../constants/UserEnums";
import { getDownloadUrl } from "./uploader";

// Type Definitions
interface UserDoc {
  id: string;
  data: () => {
    submittedForVerification?: boolean;
    userRole?: UserRoleType;
    toVerifyPhotos?: Array<{ filename: string; size: number }> | string;
    uid?: string;
    name?: string;
    email?: string;
    loc?: string;
    age?: number;
    meditationExperience?: string;
    olsExperience?: string;
    phoneNumber?: string;
    isDisabled?: boolean | string;
    disabledAt?: number;
    verificationLevel?: { [key: string]: number };
    verificationStatus?: string;
    [key: string]: any;
  };
}

interface User {
  key: string;
  uid: string;
  submittedForVerification?: boolean;
  userRole?: UserRoleType;
  toVerifyPhotos: Array<{ filename: string; size: number }>;
  verificationStatus: string;
  name?: string;
  email?: string;
  loc?: string;
  age?: number;
  meditationExperience?: string;
  olsExperience?: string;
  phoneNumber?: string;
  isDisabled: boolean;
  disabledAt?: number;
  verificationLevel?: { [key: string]: number };
  [key: string]: any;
}

interface File {
  preview: string;
  name: string;
  size: number;
}

interface FirebaseStorage {
  toVerifyPhotosStorage: (uid: string, filename: string) => string;
}

// Helper Functions
const userDocToUser = (doc: UserDoc): User => {
  const userData = doc.data();
  const isDisabledRaw = userData.isDisabled;
  let isDisabled: boolean;

  // Handle different cases for isDisabled
  if (typeof isDisabledRaw === "string") {
    isDisabled = isDisabledRaw.toLowerCase() === "true";
  } else if (typeof isDisabledRaw === "boolean") {
    isDisabled = isDisabledRaw;
  } else {
    isDisabled = false; // Default to false if undefined or invalid
  }

  // Handle toVerifyPhotos
  let toVerifyPhotos: Array<{ filename: string; size: number }> = [];
  if (typeof userData.toVerifyPhotos === "string") {
    try {
      toVerifyPhotos = userData.toVerifyPhotos === "[]" ? [] : JSON.parse(userData.toVerifyPhotos);
    } catch (e) {
      console.error("Error parsing toVerifyPhotos string:", e);
      toVerifyPhotos = [];
    }
  } else if (Array.isArray(userData.toVerifyPhotos)) {
    toVerifyPhotos = userData.toVerifyPhotos;
  } else if (!userData.toVerifyPhotos) {
    toVerifyPhotos = [];
  } else {
    console.error("Invalid toVerifyPhotos format:", userData.toVerifyPhotos);
    toVerifyPhotos = [];
  }

  const user = {
    key: doc.id,
    uid: userData.uid || doc.id,
    submittedForVerification: userData.submittedForVerification || false,
    userRole: userData.userRole ? userData.userRole.toLowerCase() as UserRoleType : UserRole.OLSS_COL, // Changed from UOLSS
    toVerifyPhotos: toVerifyPhotos,
    name: userData.name || "",
    email: userData.email || "",
    loc: userData.loc || "",
    age: userData.age || 0,
    meditationExperience: userData.meditationExperience || "",
    olsExperience: userData.olsExperience || "",
    phoneNumber: userData.phoneNumber || "",
    isDisabled: isDisabled,
    disabledAt: userData.disabledAt || 0,
    verificationLevel: userData.verificationLevel || { link: 0, photos: 0 },
    ...userData,
  };
  const verificationStatus = userData.verificationStatus || (
    user.submittedForVerification
      ? "Pending"
      : user.userRole === UserRole.ADMIN
      ? "Verified"
      : "Not Verified"
  );
  console.log("userDocToUser - Parsed user:", user, "Verification status:", verificationStatus);
  return { ...user, verificationStatus };
};

const fetchFirebasePhotos = async (
  user: User,
  firebase: FirebaseStorage,
  setFiles: React.Dispatch<React.SetStateAction<File[]>>
): Promise<void> => {
  try {
    if (!user.toVerifyPhotos || user.toVerifyPhotos.length === 0) {
      console.log("fetchFirebasePhotos - No photos to fetch for user:", user.uid);
      setFiles([]);
      return;
    }

    const promises = user.toVerifyPhotos.map(async (file) => {
      const preview = await getDownloadUrl(
        firebase.toVerifyPhotosStorage(user.uid, file.filename)
      );
      return { preview, name: file.filename, size: file.size };
    });

    const files = await Promise.all(promises);
    console.log("fetchFirebasePhotos - Fetched files:", files);
    setFiles(files);
  } catch (error) {
    console.error("Error fetching Firebase photos:", error);
    setFiles([]);
  }
};

export { userDocToUser, fetchFirebasePhotos };